import { MouseEvent, useEffect, useState } from 'react';
import { Form } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { LoadingOutlined } from '@ant-design/icons';

import If from 'global/Common/If';
import BaseSecuritiesModal from 'global/Securities/components/Modal/BaseSecuritiesModal';
import Checkbox from 'global/Securities/components/Checkbox';
import type { BaseSecuritiesModalProps } from 'global/Securities/components/Modal/BaseSecuritiesModal';
import useMultiPortfolioStore from 'lib/stores/multiPorto';
import useTermsAndConditionStore from 'global/Securities/stores/useTermsAndConditionStore';
import { TnCFeaturesIDEnum } from 'global/Securities/types/termsAndCondition';
import { CreateAccountPayload } from 'global/Securities/types/api/multiPorto';

import {
  ActionWrapper,
  AgreementTextWrapper,
  AgreementWrapper,
  Container,
  NextButton,
  TnCWrapper,
  TnCFrame,
  TnCLoadingWrapper,
} from './terms.styled';

const CreatePortoTnc = ({
  onCancel = () => {},
  onSubmit = () => {},
  ...props
}: BaseSecuritiesModalProps) => {
  const [form] = Form.useForm();
  const [tncAgreed, setTncAgreed] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [iframeLoading, setIframeLoading] = useState<boolean>(true);

  const createForm = useMultiPortfolioStore((s) => s.createPortfolioForm);
  const postCreatePorto = useMultiPortfolioStore((s) => s.postCreatePorto);

  const termsData = useTermsAndConditionStore((s) => s.termsData);
  const multiPortoTnc = termsData?.[TnCFeaturesIDEnum.MULTI_PORTO];
  const loadTnCAcceptance = useTermsAndConditionStore(
    (s) => s.loadTnCAcceptance,
  );

  const handleReset = () => {
    setTncAgreed(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    setLoading(true);
    const payload: CreateAccountPayload = {
      ...createForm,
      tnc: {
        feature_id: TnCFeaturesIDEnum.MULTI_PORTO,
        version: multiPortoTnc?.version,
      },
      idempotency_ref: `WEB-Create-Porto-${Date.now()}`,
    };

    try {
      const result = await postCreatePorto(payload);
      if (typeof result === 'boolean' && result) {
        onSubmit();
        handleReset();
      }
    } catch (err) {
      console.error('Error creating portfolio:', err);
    }

    setLoading(false);
  };

  const handleCancel = (e: MouseEvent<HTMLElement>) => {
    onCancel(e);
    handleReset();
  };

  const handleCheckboxChange = (event: CheckboxChangeEvent) => {
    setTncAgreed(event.target.checked);
  };

  useEffect(() => {
    if (props.visible) {
      loadTnCAcceptance([TnCFeaturesIDEnum.MULTI_PORTO]);
    }
  }, [props.visible, loadTnCAcceptance]);

  return (
    <BaseSecuritiesModal
      title="Syarat & Ketentuan"
      onCancel={handleCancel}
      className="modal-multi-porto"
      transitionName=""
      {...props}
    >
      <Container onClick={(e) => e.stopPropagation()}>
        <Form form={form}>
          <TnCWrapper>
            <If condition={iframeLoading}>
              <TnCLoadingWrapper>
                <LoadingOutlined
                  style={{
                    color: '#00AB6B',
                    fontSize: '20px',
                  }}
                />
              </TnCLoadingWrapper>
            </If>

            <If condition={!!multiPortoTnc?.url}>
              <TnCFrame
                title="multi-porto-tnc"
                src={multiPortoTnc?.url}
                id="tnc-multi-porto"
                onLoad={() => setIframeLoading(false)}
              />
            </If>
          </TnCWrapper>

          <Form.Item style={{ marginBottom: 0 }}>
            <AgreementWrapper>
              <Checkbox checked={tncAgreed} onChange={handleCheckboxChange} />

              <AgreementTextWrapper>
                Saya menyetujui aturan penggunaan dan Kebijakan Privasi untuk
                pemrosesan data pribadi saya di Stockbit.
              </AgreementTextWrapper>
            </AgreementWrapper>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <ActionWrapper>
              <NextButton
                onClick={handleSubmit}
                type="primary"
                disabled={!tncAgreed || loading}
                loading={loading}
              >
                Create
              </NextButton>
            </ActionWrapper>
          </Form.Item>
        </Form>
      </Container>
    </BaseSecuritiesModal>
  );
};

export default CreatePortoTnc;
